import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, LogIn, User } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useSiteContent } from '../hooks/useSiteContent';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Session 215.83 — pull the top-bar phone/email from the same
  // `tenant_home` CMS block that the Home page's Contact section
  // uses, so Super Admin → Site Content edits update everywhere at
  // once. Defaults kept in sync with Home.jsx so a brand-new
  // deployment renders the same placeholders in both places.
  const cms = useSiteContent('tenant_home', {
    contact_phone:       '(555) 123-4567',
    contact_phone_href:  'tel:5551234567',
    contact_email:       'info@zionuw.com',
    contact_email_href:  'mailto:info@zionuw.com',
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      {/* Top Bar */}
      <div className="bg-[#1e3a5f] text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex justify-end items-center gap-6 text-sm">
            <a
              href={cms.contact_phone_href || `tel:${(cms.contact_phone || '').replace(/\D/g, '')}`}
              className="flex items-center gap-2 hover:text-[#9CA3AF] transition-colors"
              data-testid="header-topbar-phone"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">{cms.contact_phone}</span>
            </a>
            <a
              href={cms.contact_email_href || `mailto:${cms.contact_email}`}
              className="flex items-center gap-2 hover:text-[#9CA3AF] transition-colors"
              data-testid="header-topbar-email"
            >
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">{cms.contact_email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/assets/zion-logo.jpeg"
              alt="Zion Underwriting Partners"
              className="h-12 sm:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-[#1e3a5f] hover:text-[#6B7280] font-medium transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-[#1e3a5f] hover:text-[#6B7280] font-medium transition-colors"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-[#1e3a5f] hover:text-[#6B7280] font-medium transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[#1e3a5f] hover:text-[#6B7280] font-medium transition-colors"
            >
              Contact
            </button>
            {/* Session 213.79 — Public-facing NTOA association landing page */}
            <Link
              to="/ntoa"
              className="text-[#1e3a5f] hover:text-[#6B7280] font-medium transition-colors"
              data-testid="header-ntoa-link"
            >
              NTOA
            </Link>
            {user ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white transition-all shadow-md hover:shadow-lg"
              >
                <User className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-[#1e3a5f] hover:text-[#6B7280]"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/quote')}
                  className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white transition-all shadow-md hover:shadow-lg"
                >
                  Get a Quote
                </Button>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[#1e3a5f] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
            <button
              onClick={() => scrollToSection('home')}
              className="text-[#1e3a5f] hover:text-[#2d9b9b] font-medium transition-colors text-left py-2"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-[#1e3a5f] hover:text-[#2d9b9b] font-medium transition-colors text-left py-2"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-[#1e3a5f] hover:text-[#2d9b9b] font-medium transition-colors text-left py-2"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[#1e3a5f] hover:text-[#2d9b9b] font-medium transition-colors text-left py-2"
            >
              Contact
            </button>
            {user ? (
              <Button
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white w-full"
              >
                <User className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-[#1e3a5f] hover:text-[#6B7280] w-full"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </Button>
                <Button
                  onClick={() => {
                    navigate('/quote');
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white w-full"
                >
                  Get a Quote
                </Button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
